import { useState, useEffect } from 'react';
import api from 'api';
import StyledTableInputWithError from './TableInput.styled';
import styles from './MilestonesAndFees.module.scss';
import * as S from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/MYCLIENTS/components/dashboard/salesOnboarding/index.styled';
import { useParams } from 'react-router-dom';
import Tip from 'views/ProviderClients/MyClients/components/Tip/Tip';
import { ReactComponent as ExclamationPointIcon } from 'assets/icons/exclamation_mark.svg';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import GenericSearchableDropdown from 'components/common/Searchabledropdown/genericSearchableDropdown';
import { currencyOptions } from 'utils/currencyOptions';

interface Props {
    handleClose?: () => void;
}

const AssessmentFee = ({ handleClose }: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { id: candidateID } = useParams();

    useEffect(() => {
        setAssessmentFee('');
        getAssessmentFee(candidateID);
    }, [candidateID]);

    const [assessmentFee, setAssessmentFee] = useState<string>('');
    const [currency, setCurrency] = useState<string>('USD');
    const [actiavteMsg, setActivateMsg] = useState<boolean>(false);
    const [activatePaidMsg, setActivatePaidMsg] = useState<boolean>(false);
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
    const isFeeValid = String(assessmentFee ?? '').trim().length > 0;

    const getAssessmentFee = async (candidate?: string) => {
        if (candidate) {
            try {
                const resp = await api.assessment_payment.getAssessmentPayment(candidate);
                if (resp.status === 200 || resp.status === 201) {
                    setAssessmentFee(String(resp?.data?.amount ?? ''));
                    if (resp?.data?.currency) {
                        setCurrency(resp?.data?.currency);
                    }
                    if (resp?.data.status === 'SUCCESSFUL') {
                        setActivatePaidMsg(true);
                        setButtonDisabled(true);
                    } else {
                        setButtonDisabled(false);
                    }
                }
            } catch (err) {
                console.error(err);
            }
        }
    };

    const saveAssessmentFee = async (
        candidate: string | undefined,
        amount: string,
        currency?: string
    ) => {
        if (candidate) {
            try {
                const resp = await api.assessment_payment.updateAssessmentFee(
                    candidate,
                    amount,
                    currency
                );
                if (resp.status === 200 || resp.status === 201) {
                    setActivateMsg(true);
                    setTimeout(() => {
                        setActivateMsg(false);
                    }, 5000);
                }
            } catch (err) {
                console.error(err);
            }
        }
    };

    return (
        <div className={styles['assessment-table-wrapper']}>
            <div
                style={{
                    backgroundColor: '#f8f7fc',
                    border: '1px solid #e8e0f5',
                    borderRadius: '8px',
                    padding: '16px 18px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                }}
            >
                <span
                    style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#475569',
                    }}
                >
                    {t('setAssessmentAgreementFee', { lng: language }) ||
                        'Set Assessment Agreement Fee'}
                </span>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) 160px',
                        gap: '12px',
                        alignItems: 'end',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <StyledTableInputWithError
                            allowOnlyIntegers={true}
                            value={assessmentFee}
                            placeholder={t('pleaseDefineFee', { lng: language })}
                            onChange={(event) => setAssessmentFee(event?.target?.value)}
                            padding="10px 14px"
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span
                            style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            }}
                        >
                            {t('currency', { lng: language }) || 'Currency'}
                        </span>
                        <GenericSearchableDropdown
                            selectedType={currency}
                            handleSelectedType={(value) => {
                                setCurrency(value);
                            }}
                            dropdownMenuPrograms={currencyOptions}
                            purple={true}
                            width="100%"
                            style={{ minHeight: '32px', padding: '4px 10px' }}
                        />
                    </div>
                </div>
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '32px',
                }}
            >
                <S.SaveChangesButton variant="outlined" onClick={() => handleClose?.()}>
                    {t('cancel', { lng: language })}
                </S.SaveChangesButton>
                <S.SaveChangesButton
                    disabled={buttonDisabled || !isFeeValid}
                    variant="contained"
                    onClick={() => {
                        if (buttonDisabled || !isFeeValid) {
                            return;
                        }
                        saveAssessmentFee(candidateID, assessmentFee, currency);
                        handleClose?.();
                    }}
                >
                    {t('save', { lng: language })}
                </S.SaveChangesButton>
            </div>

            {actiavteMsg && (
                <Tip icon={ExclamationPointIcon}>{t('assessmentFeeSet', { lng: language })}</Tip>
            )}
            {activatePaidMsg && (
                <Tip icon={ExclamationPointIcon}>
                    {t('assessmentFeeAlreadyPaid', { lng: language })}
                </Tip>
            )}
        </div>
    );
};

export default AssessmentFee;
